import { Link } from 'react-router-dom'
import { ShoppingBag } from 'lucide-react'
import { assetUrl } from '../../lib/http'
import { pkr } from '../../utils/format'
import { AccountPanel, Pill } from '../../components/account/AccountUi'
import { day, statusOf } from '../../components/account/status'
import { EmptyAccount, Loading, Problem } from '../../components/account/AccountStates'
import { useMyBookings } from '../../hooks/useAccountQueries'
import type { AccountBooking } from '../../data/account'

/* The orders in this person's name, newest first.

   A card per order, and the part that matters is the money: what the car was
   agreed at, what has been paid against it, and what is left. The payments
   are listed under it rather than summarised, because a buyer checking this
   page is usually checking whether a particular transfer landed — and the
   reference they are looking for is the one they typed at the bank. */

function BookingCard({ booking }: { booking: AccountBooking }) {
  const status = statusOf(booking.status)
  const car = booking.vehicle

  return (
    <article className="overflow-hidden rounded-2xl border border-line bg-raised/60">
      <div className="flex flex-wrap items-start gap-5 p-5 sm:p-6">
        <span className="block h-20 w-28 shrink-0 overflow-hidden rounded-xl bg-surface">
          {car?.image ? (
            <img
              src={assetUrl(car.image)}
              alt=""
              loading="lazy"
              className="size-full object-contain p-1"
            />
          ) : null}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <h3 className="font-display text-[17px] font-semibold tracking-[-0.02em] text-platinum">
              {car ? `${car.title}` : 'A car we have since removed'}
            </h3>
            <Pill tone={status.tone}>{status.label}</Pill>
          </div>

          <p className="mt-1 text-[13px] text-dim">
            {car ? `${car.year}${car.colour ? ` · ${car.colour}` : ''} · ` : ''}
            Order {booking.reference || booking.id.slice(0, 8)} · placed {day(booking.createdAt)}
          </p>

          {status.hint ? <p className="mt-3 text-[13.5px] text-steel">{status.hint}</p> : null}
        </div>
      </div>

      {/* The three figures, on their own band so they read as the answer
          rather than as more of the description. */}
      <dl className="grid grid-cols-3 gap-px border-t border-line bg-line">
        <div className="bg-raised px-5 py-4">
          <dt className="label-sm text-steel">Agreed</dt>
          <dd className="mt-1.5 text-[15px] font-semibold text-platinum tabular-nums">
            {booking.total > 0 ? `PKR ${pkr.format(booking.total)}` : '—'}
          </dd>
        </div>
        <div className="bg-raised px-5 py-4">
          <dt className="label-sm text-steel">Paid</dt>
          <dd className="mt-1.5 text-[15px] font-semibold text-lime tabular-nums">
            PKR {pkr.format(booking.paid)}
          </dd>
        </div>
        <div className="bg-raised px-5 py-4">
          <dt className="label-sm text-steel">Balance</dt>
          <dd className="mt-1.5 text-[15px] font-semibold text-platinum tabular-nums">
            {booking.total > 0 ? `PKR ${pkr.format(booking.balance)}` : '—'}
          </dd>
        </div>
      </dl>

      {booking.payments.length > 0 ? (
        <div className="border-t border-line px-5 py-4 sm:px-6">
          <p className="label-sm text-steel">Payments received</p>
          <ul className="mt-3 flex flex-col gap-2">
            {booking.payments.map((payment) => (
              <li
                key={payment.id}
                className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-[13.5px]"
              >
                <span className="text-platinum tabular-nums">
                  PKR {pkr.format(payment.amount)}
                </span>
                <span className="text-dim">
                  {day(payment.paidAt)}
                  {payment.method ? ` · ${payment.method.replace(/-/g, ' ')}` : ''}
                  {payment.reference ? ` · ${payment.reference}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </article>
  )
}

export function AccountBookings() {
  const query = useMyBookings()

  if (query.isPending) return <Loading label="Loading your orders" />
  if (query.isError) return <Problem onRetry={() => void query.refetch()} />

  const bookings = query.data ?? []

  if (bookings.length === 0) {
    return (
      <EmptyAccount
        icon={<ShoppingBag className="size-5" strokeWidth={1.8} />}
        title="No orders yet"
        body="When you book a car with us it appears here, with every payment against it."
        action={
          <Link
            to="/home#cars"
            className="text-[13.5px] font-medium text-lime hover:text-lime-lift"
          >
            Browse the floor
          </Link>
        }
      />
    )
  }

  return (
    <AccountPanel className="!border-0 !bg-transparent !p-0">
      <div className="flex flex-col gap-4">
        {bookings.map((booking) => (
          <BookingCard key={booking.id} booking={booking} />
        ))}
      </div>
    </AccountPanel>
  )
}

export default AccountBookings
